import { MenuItem } from '@/types';

// 1. 定义基础路由类型
interface RouteMeta {
  showBreadcrumb?: boolean;
  showEnterpriseSearch?: boolean;
  keepAlive?: boolean;
}

type BaseRoute = Omit<MenuItem, 'meta'> & {
  meta?: RouteMeta;
};

const WATER_JELLYFISH_REPORT_PLUS_NAME = '\u6c34\u6bcd\u62a5\u544aPlus';

// 2. 提取公共路径前缀
const PATHS = {
  WALLET: '/user-center/wallet',
  SEARCH_HISTORY: '/user-center/search-history',
  MESSAGE: '/user-center/message',
  SECURITY: '/user-center/security',
} as const;

// 3. 定义可复用的路由组件
const COMPONENTS = {
  RECHARGE: '../views/UserCenter/pages/Wallet/pages/Recharge',
  CONSUMER_PRICE: '../views/UserCenter/pages/Wallet/pages/ConsumerPrice',
  CAR_SEARCH: '../views/Search/pages/CarSearch',
  BUSINESS: '../views/Search/pages/Business',
  RISK: '../views/Search/pages/Risk',
  ANNUAL_REPORT: '../views/Search/pages/AnnualReport',
  LAWSUIT: '../views/Search/pages/Lawsuit',
  REAL_ESTATE_ASSESSMENT: '../views/Search/pages/RealEstateAssessment',
  HISTORICAL_TRANSACTION: '../views/Search/pages/HistoricalTransaction',
  MARITAL: '../views/Search/pages/Marital',
  VERIFICATION_FOUR: '../views/Search/pages/VerificationFour',
  VERIFICATION_THREE: '../views/Search/pages/VerificationThree',
  REAL_ESTATE: '../views/Search/pages/RealEstate',
  SOCIAL_SECURITY: '../views/Search/pages/SocialSecurity',
  EDUCATION_INFO: '../views/Search/pages/EducationInfo',
  BIG_DATA: '../views/Search/pages/BigData',
  DIY_SEARCH_ABSTRACT: '../views/Search/pages/DiySearchAbstractPage',
  ENTERPRISE_TAX_INVOICE: '../views/Search/pages/EnterpriseTaxInvoice',
} as const;

// 4. 创建路由生成器函数
const createRoute = (
  path: string,
  name: string,
  element: string,
  meta: RouteMeta = {},
): BaseRoute => ({
  authorityKey: '',
  path,
  name,
  element,
  meta: { showBreadcrumb: true, ...meta },
});

const createEnterpriseRoute = (
  basePath: string,
  name: string,
  element: string = COMPONENTS.BUSINESS,
) => ({
  business: createRoute(`${basePath}/business/:recordNo`, name, element, {
    showEnterpriseSearch: true,
    keepAlive: true,
  }),
  risk: createRoute(`${basePath}/business/:recordNo/risk/:entName`, '扫描风险', COMPONENTS.RISK, {
    showEnterpriseSearch: true,
    keepAlive: true,
  }),
  annualReport: createRoute(
    `${basePath}/business/:recordNo/annual-report/:entName/:year`,
    '企业年报',
    COMPONENTS.ANNUAL_REPORT,
    { showEnterpriseSearch: true, keepAlive: true },
  ),
});

const createAbstractRoutes = (basePath: string) => {
  const routes = [
    createRoute(`${basePath}/abstract/:initiateOrderNo`, '摘要页', COMPONENTS.DIY_SEARCH_ABSTRACT, {
      keepAlive: true,
    }),

    // 企业相关路由
    ...Object.values(createEnterpriseRoute(`${basePath}/abstract/:initiateOrderNo`, '查工商')),

    // 诉讼相关
    createRoute(
      `${basePath}/abstract/:initiateOrderNo/lawsuit/:recordNo?`,
      '个人诉讼详情',
      COMPONENTS.LAWSUIT,
    ),
    createRoute(
      `${basePath}/abstract/:initiateOrderNo/enterprise-lawsuit/:recordNo`,
      '企业诉讼详情',
      COMPONENTS.LAWSUIT,
    ),

    // 其他详情页
    createRoute(
      `${basePath}/abstract/:initiateOrderNo/real-estate-assessment/:recordNo?`,
      '房产评估',
      COMPONENTS.REAL_ESTATE_ASSESSMENT,
    ),
    createRoute(
      `${basePath}/abstract/:initiateOrderNo/historical-transaction/:recordNo?`,
      '历史成交',
      COMPONENTS.HISTORICAL_TRANSACTION,
    ),
    createRoute(
      `${basePath}/abstract/:initiateOrderNo/marital/:recordNo?`,
      '婚姻状况',
      COMPONENTS.MARITAL,
    ),
    createRoute(
      `${basePath}/abstract/:initiateOrderNo/verification4/:recordNo?`,
      '四要素详情',
      COMPONENTS.VERIFICATION_FOUR,
    ),
    createRoute(
      `${basePath}/abstract/:initiateOrderNo/verification3/:recordNo?`,
      '三要素详情',
      COMPONENTS.VERIFICATION_THREE,
    ),
    createRoute(
      `${basePath}/abstract/:initiateOrderNo/real-estate/:recordNo?`,
      '名下房产',
      COMPONENTS.REAL_ESTATE,
    ),
    createRoute(
      `${basePath}/abstract/:initiateOrderNo/social-security/:recordNo?`,
      '广东社保',
      COMPONENTS.SOCIAL_SECURITY,
    ),

    // 车辆相关路由（可进一步提取）
    createRoute(
      `${basePath}/abstract/:initiateOrderNo/enterprise-taxInvoice/:recordNo?`,
      WATER_JELLYFISH_REPORT_PLUS_NAME,
      COMPONENTS.ENTERPRISE_TAX_INVOICE,
    ),
  ];

  // 车辆相关路由（统一处理）
  const carRoutes = [
    { suffix: 'myCar', name: '名下车辆' },
    { suffix: 'ownerVerification', name: '人/企车主核验' },
    { suffix: 'transferNum', name: '过户次数' },
    { suffix: 'drivingPermit', name: '行驶证查询' },
    { suffix: 'carFiveItems', name: '车五项查询' },
    { suffix: 'carInsurance', name: '车辆保险查询' },
    { suffix: 'accidentRecords', name: '出险详情记录' },
    { suffix: 'usedCar', name: '二手车评估' },
  ];

  carRoutes.forEach(({ suffix, name }) => {
    routes.push(
      createRoute(
        `${basePath}/abstract/:initiateOrderNo/${suffix}/:recordNoData?`,
        name,
        COMPONENTS.CAR_SEARCH,
      ),
    );
  });

  return routes;
};

const createCommonSearchRoutes = (basePath: string) => {
  const routes = [
    ...Object.values(createEnterpriseRoute(basePath, '查工商')),
    createRoute(`${basePath}/lawsuit/:recordNo?`, '个人诉讼详情', COMPONENTS.LAWSUIT),
    createRoute(`${basePath}/enterprise-lawsuit/:recordNo`, '企业诉讼详情', COMPONENTS.LAWSUIT),
    createRoute(
      `${basePath}/real-estate-assessment/:recordNo?`,
      '房产评估',
      COMPONENTS.REAL_ESTATE_ASSESSMENT,
    ),
    createRoute(
      `${basePath}/historical-transaction/:recordNo?`,
      '历史成交',
      COMPONENTS.HISTORICAL_TRANSACTION,
    ),
    createRoute(`${basePath}/marital/:recordNo?`, '婚姻状况', COMPONENTS.MARITAL),
    createRoute(`${basePath}/verification4/:recordNo?`, '四要素详情', COMPONENTS.VERIFICATION_FOUR),
    createRoute(
      `${basePath}/verification3/:recordNo?`,
      '三要素详情',
      COMPONENTS.VERIFICATION_THREE,
    ),
    createRoute(`${basePath}/real-estate/:recordNo?`, '名下房产', COMPONENTS.REAL_ESTATE),
    createRoute(`${basePath}/social-security/:recordNo?`, '广东社保', COMPONENTS.SOCIAL_SECURITY),
    createRoute(`${basePath}/education/:recordNo?`, '学历信息', COMPONENTS.EDUCATION_INFO),
    createRoute(`${basePath}/bigdata/:recordNo?`, '大数据', COMPONENTS.BIG_DATA),
    createRoute(
      `${basePath}/enterprise-taxInvoice/:recordNo?`,
      WATER_JELLYFISH_REPORT_PLUS_NAME,
      COMPONENTS.ENTERPRISE_TAX_INVOICE,
    ),
  ];

  // 车辆相关路由（统一处理）
  const carRoutes = [
    { suffix: 'myCar', name: '名下车辆详情' },
    { suffix: 'ownerVerification', name: '人/企车主核验详情' },
    { suffix: 'transferNum', name: '过户次数详情' },
    { suffix: 'drivingPermit', name: '行驶证查询详情' },
    { suffix: 'carFiveItems', name: '车五项查询详情' },
    { suffix: 'carInsurance', name: '车辆保险查询详情' },
    { suffix: 'accidentRecords', name: '出险详情记录详情' },
    { suffix: 'usedCar', name: '二手车评估详情' },
  ];

  carRoutes.forEach(({ suffix, name }) => {
    routes.push(
      createRoute(`${basePath}/carSearch/${suffix}/:recordNoData?`, name, COMPONENTS.CAR_SEARCH),
    );
  });

  return routes;
};

// 5. 构建完整路由配置
const routes: MenuItem[] = [
  // 钱包路由
  createRoute(`${PATHS.WALLET}/recharge`, '充值', COMPONENTS.RECHARGE),
  createRoute(`${PATHS.WALLET}/consumerPrice`, '消费价目表', COMPONENTS.CONSUMER_PRICE),

  // 查询历史路由
  ...createCommonSearchRoutes(PATHS.SEARCH_HISTORY),
  ...createAbstractRoutes(PATHS.SEARCH_HISTORY),

  // 消息路由（与查询历史结构相同）
  ...createCommonSearchRoutes(PATHS.MESSAGE),
  ...createAbstractRoutes(PATHS.MESSAGE),

  // 安全中心路由
  createRoute(
    `${PATHS.SECURITY}/enterprise-taxInvoice/:recordNo?`,
    WATER_JELLYFISH_REPORT_PLUS_NAME,
    COMPONENTS.ENTERPRISE_TAX_INVOICE,
  ),
];

export default routes;
